import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import MDBadge from 'components/MDBadge';
import TopNavbar from 'examples/Navbars/TopNavbar';
import MDButton from '../../components/MDButton';
import { useData } from '../../hooks';
import { handleDownloadExcel } from '../../components/excel/excelUtils';
import Tooltip from '@mui/material/Tooltip';
import {
  apiGetKeitiIncentiveList,
  apiGetKeitiIncentiveSummary,
  apiGetKeitiIncentiveHealth,
} from '../../api/keiti.api';

/** 전송 상태 판을 몇 일치 보여줄지 */
const HEALTH_WINDOW_DAYS = 60;
/** 격자 한 줄에 몇 칸 — 60일이 30×2 로 딱 떨어진다 */
const HEALTH_GRID_COLUMNS = 30;

const getTodayYmd = () => new Date().toISOString().split('T')[0];

/** rlvtDe(yyyyMMdd) -> yyyy-MM-dd */
const formatRlvtDe = (rlvtDe) => {
  if (!rlvtDe || rlvtDe.length !== 8) return rlvtDe || '-';
  return `${rlvtDe.slice(0, 4)}-${rlvtDe.slice(4, 6)}-${rlvtDe.slice(6, 8)}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const KeitiIncentiveList = () => {
  const [incentives, setIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('');
  const [rlvtDeFrom, setRlvtDeFrom] = useState('');
  const [rlvtDeTo, setRlvtDeTo] = useState('');

  const [summary, setSummary] = useState({ totalCount: 0, totalPetBottles: 0, totalAcrsCo: 0 });

  // 전송 상태(결측 포함). 목록 필터와 무관하게 항상 최근 N일을 본다.
  const [health, setHealth] = useState(null);
  const [healthError, setHealthError] = useState('');

  const { user, appIsReady } = useData();

  useEffect(() => {
    const today = getTodayYmd();
    setRlvtDeFrom(today);
    setRlvtDeTo(today);
  }, []);

  useEffect(() => {
    if (rlvtDeFrom && rlvtDeTo && appIsReady && user) {
      fetchIncentives(page);
      fetchSummary();
    }
  }, [page, user, appIsReady, rlvtDeFrom, rlvtDeTo]);

  // 전송 상태는 검색 조건과 무관하다 — 화면에 들어올 때 한 번만 부른다.
  useEffect(() => {
    if (!appIsReady || !user) return;
    apiGetKeitiIncentiveHealth(HEALTH_WINDOW_DAYS, user.accessToken)
      .then((res) => {
        setHealth(res);
        setHealthError('');
      })
      .catch((e) => {
        // 조용히 실패하면 "이상 없음"으로 오해한다. 화면에 그대로 드러낸다.
        setHealth(null);
        setHealthError(e?.response?.data?.reason || '전송 상태를 불러오지 못했습니다.');
      });
  }, [user, appIsReady]);

  const fetchIncentives = async (pageNum) => {
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        limit: 20,
        phone: phone || undefined,
        status: status || undefined,
        rlvtDeFrom: rlvtDeFrom ? rlvtDeFrom.replace(/-/g, '') : undefined,
        rlvtDeTo: rlvtDeTo ? rlvtDeTo.replace(/-/g, '') : undefined,
      };

      const response = await apiGetKeitiIncentiveList(params, user.accessToken);
      if (response) {
        setIncentives(response.data || []);
        setTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Failed to load keiti incentives:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = {
        rlvtDeFrom: rlvtDeFrom ? rlvtDeFrom.replace(/-/g, '') : undefined,
        rlvtDeTo: rlvtDeTo ? rlvtDeTo.replace(/-/g, '') : undefined,
      };
      const response = await apiGetKeitiIncentiveSummary(params, user.accessToken);
      if (response) {
        setSummary(response);
      }
    } catch (error) {
      console.error('Failed to load summary:', error);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchIncentives(1);
    fetchSummary();
  };

  const columns = [
    { Header: 'ID', accessor: 'id', align: 'left' },
    { Header: '기준일자', accessor: 'rlvtDe', align: 'left' },
    { Header: '요청번호', accessor: 'requestNo', align: 'left' },
    { Header: '전화번호', accessor: 'phone', align: 'left' },
    { Header: '매칭ID', accessor: 'mtchgId', align: 'left' },
    { Header: '페트병 수', accessor: 'petBottleCount', align: 'center' },
    { Header: '인센티브 단위', accessor: 'acrsCo', align: 'center' },
    { Header: '상태', accessor: 'status', align: 'center' },
    { Header: '처리일시', accessor: 'createdAt', align: 'left' },
  ];

  const rows = incentives.map((row) => ({
    id: row.id,
    rlvtDe: formatRlvtDe(row.rlvtDe),
    requestNo: row.requestNo,
    phone: row.phone || '-',
    mtchgId: row.mtchgId,
    petBottleCount: row.petBottleCount,
    acrsCo: row.acrsCo,
    status: (
      <MDBadge
        badgeContent={row.status}
        color={row.status === 'SUCCESS' ? 'success' : 'error'}
        variant="gradient"
        size="sm"
      />
    ),
    createdAt: formatDate(row.createdAt),
  }));

  return (
    <DashboardLayout>
      <TopNavbar />

      <MDBox display="flex" mr={2} justifyContent="right">
        <MDButton
          variant="gradient"
          color="info"
          onClick={() => handleDownloadExcel('탄소중립 인센티브 내역', columns, rows)}
          sx={{ ml: 2 }}
        >
          엑셀 다운로드
        </MDButton>
      </MDBox>

      {/*
        🔴 전송 상태 판 — 아래 목록은 "있는 행"만 보여주므로 결측이 보이지 않는다.
        실패하면 행 자체가 생기지 않아 상태 컬럼은 늘 SUCCESS 뿐이었고,
        그래서 60일 미전송을 아무도 몰랐다. 여기서는 "안 보낸 날"을 빨간 칸으로 드러낸다.
      */}
      {/* 아래 목록 카드와 좌우 폭을 맞추려고 같은 Grid 구조를 쓴다 */}
      <MDBox pt={3}>
        <Grid container spacing={6}>
        <Grid item xs={12}>
        <Card>
          <MDBox p={2}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <MDTypography variant="h6">
                KEITI 전송 상태 — 최근 {HEALTH_WINDOW_DAYS}일
              </MDTypography>
              {health && (
                <MDBadge
                  badgeContent={
                    health.consecutiveMissingDays > 0
                      ? `${health.consecutiveMissingDays}일째 미전송`
                      : '오늘 전송됨'
                  }
                  color={health.consecutiveMissingDays > 0 ? 'error' : 'success'}
                  variant="gradient"
                  size="sm"
                />
              )}
            </MDBox>

            {healthError && (
              <MDTypography variant="button" color="error" fontWeight="medium">
                {healthError}
              </MDTypography>
            )}

            {health && (
              <>
                <MDBox display="flex" gap={3} flexWrap="wrap" mt={1} mb={2}>
                  <MDTypography variant="button" color="text">
                    마지막 전송일{' '}
                    <MDTypography variant="button" fontWeight="bold">
                      {formatRlvtDe(health.lastSentDate) || '-'}
                    </MDTypography>
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    미전송{' '}
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      color={health.missingCount > 0 ? 'error' : 'success'}
                    >
                      {health.missingCount}일
                    </MDTypography>{' '}
                    / {HEALTH_WINDOW_DAYS}일
                  </MDTypography>
                </MDBox>

                {/*
                  🔴 열 수를 고정한다. flex-wrap 으로 두면 창 너비에 따라 줄바꿈 위치가
                  매번 달라져 마지막 줄에 몇 칸만 남고, 그것이 버그처럼 보인다.
                  30칸 × 2줄이면 60일이 항상 같은 모양으로 읽힌다.
                */}
                <MDBox
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${HEALTH_GRID_COLUMNS}, 14px)`,
                    gap: '3px',
                    width: 'fit-content',
                  }}
                >
                  {health.days.map((d) => (
                    <Tooltip
                      key={d.date}
                      title={`${formatRlvtDe(d.date)} · ${
                        d.sent ? `${d.rows.toLocaleString()}건 전송` : '미전송'
                      }`}
                    >
                      <MDBox
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '3px',
                          backgroundColor: d.sent ? '#4CAF50' : '#F44336',
                        }}
                      />
                    </Tooltip>
                  ))}
                </MDBox>

                <MDTypography variant="caption" color="text" mt={1} display="block">
                  왼쪽이 오래된 날, 오른쪽이 오늘. 🟩 전송 · 🟥 미전송 — 칸에 마우스를 올리면 날짜와 건수가 나옵니다.
                </MDTypography>
              </>
            )}
          </MDBox>
        </Card>
        </Grid>
        </Grid>
      </MDBox>

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  탄소중립 포인트 인센티브 내역
                </MDTypography>
              </MDBox>

              {/* 요약 통계 카드 */}
              <MDBox p={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ p: 2, textAlign: 'center' }}>
                      <MDTypography variant="caption" color="text">
                        총 건수
                      </MDTypography>
                      <MDTypography variant="h5">{summary.totalCount.toLocaleString()}</MDTypography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ p: 2, textAlign: 'center' }}>
                      <MDTypography variant="caption" color="text">
                        총 페트병 수
                      </MDTypography>
                      <MDTypography variant="h5">
                        {summary.totalPetBottles.toLocaleString()}
                      </MDTypography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card sx={{ p: 2, textAlign: 'center' }}>
                      <MDTypography variant="caption" color="text">
                        총 인센티브 단위
                      </MDTypography>
                      <MDTypography variant="h5">{summary.totalAcrsCo.toLocaleString()}</MDTypography>
                    </Card>
                  </Grid>
                </Grid>
              </MDBox>

              {/* 필터 영역 */}
              <MDBox p={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="전화번호"
                      fullWidth
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="010..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="상태"
                      select
                      fullWidth
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      /*
                       * value="" 는 "전체"라는 뜻이지 미선택이 아니다.
                       * displayEmpty 가 없으면 칸이 비어 보이고, 라벨은 테두리에 걸쳐
                       * 고장난 것처럼 보인다. 라벨을 항상 위로 올려 옆의 날짜 칸과 모양을 맞춘다.
                       */
                      SelectProps={{ displayEmpty: true }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">전체</MenuItem>
                      <MenuItem value="SUCCESS">SUCCESS</MenuItem>
                      <MenuItem value="FAIL">FAIL</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="기준일 From"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={rlvtDeFrom}
                      onChange={(e) => setRlvtDeFrom(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="기준일 To"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={rlvtDeTo}
                      onChange={(e) => setRlvtDeTo(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button variant="contained" color="success" fullWidth onClick={handleSearch}>
                      검색
                    </Button>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  loading={loading}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
};

export default KeitiIncentiveList;
